import * as React from 'react';
import type { EnterHook, Router } from 'react-router';
import { IndexRoute, Redirect, Route } from 'react-router';

import { omit } from 'lodash';

import { Route as CourseraRoute } from '@coursera/react-router';

import ClipsApp from 'bundles/clips/components/ClipsApp';
import loadOnRoute from 'bundles/common/components/loadOnRoute';
import LearnerOnboardingPage from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import OrgLandingPage from 'bundles/org-landing/pages/OrgLandingPageContent';
import OrgLandingApp from 'bundles/org-landing/pages/OrgLandingRoot';
import userIdentity from 'bundles/phoenix/template/models/userIdentity';
import ProgramHomeApp from 'bundles/program-home/components/ProgramHomeApp';
import EnterpriseDomainsPage from 'bundles/program-home/components/enterprise-home/EnterpriseDomainsPage';
import EnterpriseHomeApp from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import EnterpriseHomePage from 'bundles/program-home/components/enterprise-home/EnterpriseHomePage';
import AllCollectionsPage from 'bundles/program-home/components/single-program/AllCollectionsPage';
import RouteNames from 'bundles/program-home/constants/RouteNames';
import ProgramSkillRecommendationsPage from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/ProgramSkillRecommendationsPage';
import ProgramSkillSetSkillRecommendationsPage from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/ProgramSkillSetSkillRecommendationsPage';

import { ProgramHomePageTypes } from './constants/ProgramHomeConstants';

const EnterpriseSearchPage = loadOnRoute(() => import('bundles/program-home/components/search/EnterpriseSearchPage'));

const ProgramHomeSearchPage = loadOnRoute(() => import('bundles/program-home/components/search/ProgramHomeSearchPage'));

const CollectionDetailsContainer = loadOnRoute(
  () => import('bundles/enterprise-collections/components/CollectionDetailsContainer')
);

const OldModalRouteRedirect = loadOnRoute(
  () => import('bundles/enterprise-collections/components/OldModalRouteRedirect')
);

const ProgramSkillsObjectivesPage = loadOnRoute(
  () => import('bundles/program-skills-objectives/components/ProgramSkillsObjectivesPage/ProgramSkillsObjectivesPage')
);

const OrgPrivacyNoticePage = loadOnRoute(
  () => import('bundles/program-home/components/org-privacy-notice/OrgPrivacyNoticePage')
);

const ClipsRootContainer = () => import('bundles/clips/components/ClipsRootContainer');

const NotFound = loadOnRoute(() => import('bundles/phoenix/components/NotFound'));

const scrollToTopOnNavigate: Router.ChangeHook = (prevState, nextState) => {
  if (nextState.location.action !== 'PUSH') {
    // history navigation
    return;
  }

  if (nextState.location.pathname !== prevState.location.pathname) {
    // always on path navigation
    window.scrollTo(0, 0);
  }
};

type GetComponentFunc = NonNullable<Route.RouteProps['getComponent']>;

// If a user pastes the url wrong (e.g. twice), we'll have an array value for query. Normally this is OK
// (the page will crash), but the terrible Algolia library does some weirdo SSR render that triggers
// infra alerts. If we detect this case, return a NotFound, squelching the errors.
const GuardedProgramHomeSearchPage: GetComponentFunc = (nextState, callback) => {
  const query = nextState.location.query?.query;
  const getComponent: GetComponentFunc = Array.isArray(query) ? NotFound : ProgramHomeSearchPage;
  return getComponent(nextState, callback);
};

const onEnterProgramHome: EnterHook = (nextState, replace) => {
  const { deepLinkItemId, attemptSSOLogin, itemId, productType } = nextState.location.query;

  // If the user is about to be logged in through SSO with an itemId, add the deeplink param for consumption by the
  // minimodal for autoenrollment and deeplinking
  if (!deepLinkItemId && attemptSSOLogin && itemId && productType === 'course') {
    replace({
      pathname: nextState.location.pathname,
      query: { ...omit(nextState.location.query, 'itemId'), deepLinkItemId: itemId },
    });
  }
};

const routes = (
  <CourseraRoute
    onChange={scrollToTopOnNavigate}
    handle={{
      tracking: {
        data: () => ({
          page: { segment: 'enterprise' },
        }),
      },
    }}
  >
    <Route name={RouteNames.ENTERPRISE_LEARNER_ROOT} path="programs/all" component={EnterpriseHomeApp}>
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_SEARCH}
        path=":orgSlug/browse"
        getComponent={EnterpriseSearchPage}
        handle={{
          tracking: {
            data: (_, location) => {
              const currentPage = location?.query?.page;

              return {
                page: {
                  type: ProgramHomePageTypes.ENTERPRISE_SEARCH,
                  pagination: {
                    pageNumber: currentPage ? Number(currentPage) : 1,
                  },
                },
              };
            },
          },
        }}
      />
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_DOMAINS}
        path=":orgSlug/:domainSlug(/:subdomainSlug)"
        component={EnterpriseDomainsPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.SKILLSETS },
            }),
          },
        }}
      />
      <CourseraRoute
        name={RouteNames.ENTERPRISE_LEARNER_HOME}
        path=":orgSlug"
        component={EnterpriseHomePage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.HOME },
            }),
          },
        }}
      />
    </Route>

    <CourseraRoute
      name={RouteNames.ENTERPRISE_LEARNER_LANDING_ROOT}
      path="programs/landing/:orgSlug"
      component={OrgLandingApp}
      handle={{
        tracking: {
          data: () => ({
            page: { type: ProgramHomePageTypes.HOME },
          }),
        },
      }}
    >
      <IndexRoute name={RouteNames.ENTERPRISE_LEARNER_LANDING} component={OrgLandingPage} />
    </CourseraRoute>

    <CourseraRoute
      name={RouteNames.ENTERPRISE_PRIVACY_NOTICE}
      path="programs/privacy-notice/:orgSlug"
      getComponent={OrgPrivacyNoticePage}
      handle={{
        tracking: {
          data: () => ({
            page: { type: ProgramHomePageTypes.HOME },
          }),
        },
      }}
    />

    <CourseraRoute
      name={RouteNames.PROGRAM_HOME}
      path="programs(/:programSlug)"
      component={ProgramHomeApp}
      onEnter={onEnterProgramHome}
      handle={{
        tracking: {
          data: (_, location) => {
            const currentTab = location?.query?.currentTab as keyof typeof ProgramHomePageTypes;

            return {
              page: { type: ProgramHomePageTypes[currentTab] ?? ProgramHomePageTypes.CATALOG },
            };
          },
        },
      }}
    >
      <CourseraRoute
        name={RouteNames.COLLECTION_DETAILS}
        path="collections"
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.RECOMMENDATIONS },
            }),
          },
        }}
      >
        <Route path=":collectionId" getComponent={CollectionDetailsContainer} />
        <Route path=":contextType/:contextId" getComponent={CollectionDetailsContainer} />
      </CourseraRoute>

      <Redirect from="objectives/:objectiveSlug" to="skillsets/:objectiveSlug" />

      <CourseraRoute
        name={RouteNames.OBJECTIVES}
        path="skillsets/:objectiveSlug"
        getComponent={ProgramSkillsObjectivesPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.PROGRAM_SKILLSETS },
            }),
          },
        }}
      />

      <CourseraRoute
        name={RouteNames.SKILLSET_SKILL_RECOMMENDATIONS}
        path="skillsets/:objectiveSlug/skill/:skillId"
        component={ProgramSkillSetSkillRecommendationsPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.PROGRAM_SKILLSETS },
            }),
          },
        }}
      />

      <CourseraRoute
        name={RouteNames.SKILL_RECOMMENDATIONS}
        path="skills/:skillId"
        component={ProgramSkillRecommendationsPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.PROGRAM_SKILLS },
            }),
          },
        }}
      />

      <CourseraRoute
        name={RouteNames.VIEW_ALL_COLLECTIONS}
        path="view-all-collections(/:collectionType)"
        component={AllCollectionsPage}
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.RECOMMENDATIONS },
            }),
          },
        }}
      />

      <CourseraRoute
        name={RouteNames.BROWSE}
        path="browse"
        getComponent={GuardedProgramHomeSearchPage}
        handle={{
          tracking: {
            data: (_, location) => {
              const currentPage = location?.query?.page;

              return {
                page: {
                  type: ProgramHomePageTypes.ENTERPRISE_SEARCH,
                  pagination: {
                    pageNumber: currentPage ? Number(currentPage) : 1,
                  },
                },
              };
            },
          },
        }}
      />

      <Route name={RouteNames.LEARNER_ONBOARDING} path="onboarding" component={LearnerOnboardingPage} />

      <Route name="profile" path="login">
        {/* TODO: let this route handle login, put redirect in a willTransitionTo that checks for authentication */}
        <Redirect to={userIdentity.getProfileUrl()} />
      </Route>
      <Route
        name={RouteNames.MINI_PAGE}
        path="modal(/:type(/:id(/:actionType)))"
        getComponent={OldModalRouteRedirect}
      />
      <CourseraRoute
        name={RouteNames.DOMAIN_SUBDOMAIN_COLLECTIONS}
        path=":domainSlug(/:subdomainSlug)"
        handle={{
          tracking: {
            data: () => ({
              page: { type: ProgramHomePageTypes.PROGRAM_SKILLS },
            }),
          },
        }}
      />
    </CourseraRoute>
    <Route path="/" component={ClipsApp}>
      <Route path="clips/:courseSlug/:itemId" getComponent={loadOnRoute(ClipsRootContainer)} />;
      <Route path="videos/:courseSlug/:itemId" getComponent={loadOnRoute(ClipsRootContainer)} />;
    </Route>
    <Route path="*" getComponent={NotFound} />
  </CourseraRoute>
);

export default routes;
